/** Mirrors BackOffice\PricingTierResource in kashround-api. */
export interface PricingTier {
  uuid: string;
  name: string;
  pricingModel: string;
  price: number;
  /** decimal:2 on the model, so the API sends a string. */
  pricePerUser: string | null;
  currency: string;
  billingCycle: string;
  maxUsers: number | null;
  minUsers: number | null;
  trialDays: number;
  isFree: boolean;
  isActive: boolean;
  features: string[];
  /** Subscriptions that are neither canceled nor expired. */
  subscriberCount: number;
  hasSubscribers: boolean;
  /** Snake_case field names the API would refuse. Empty when unlocked. */
  lockedFields: string[];
}

export interface PricingTierMeta {
  pricingModels: string[];
  billingCycles: string[];
}

const CYCLE_NOUN: Record<string, string> = { annual: 'year', monthly: 'month' };

/**
 * The plan's list price in words, in the tier's OWN currency. This portal never
 * converts. A per_user tier prices per member, so rendering its flat `price`
 * would report the largest accounts as free.
 */
export function formatTierPrice(
  tier: PricingTier,
  format: (amount: number, currency: string) => string,
): string {
  if (tier.isFree) {
    return 'Free';
  }

  const per = ` per ${CYCLE_NOUN[tier.billingCycle] ?? tier.billingCycle}`;

  if (tier.pricingModel === 'per_user') {
    return tier.pricePerUser === null
      ? 'Not set'
      : `${format(Number(tier.pricePerUser), tier.currency)} per user${per}`;
  }

  return `${format(tier.price, tier.currency)}${per}`;
}

/** "11 to 30 members", "Up to 30 members", "11 or more members". */
export function formatSeatRange(tier: PricingTier): string {
  const { minUsers, maxUsers } = tier;
  if (minUsers === null && maxUsers === null) return 'Any number of members';
  if (minUsers === null) return `Up to ${maxUsers} members`;
  if (maxUsers === null) return `${minUsers} or more members`;
  return `${minUsers} to ${maxUsers} members`;
}
